import { owaComputedFn } from 'owa-computed-fn';
import type {
    AppButtonProps,
    AppCheckboxOnExecuteParameter,
    AppCheckboxProps,
    AppCompoundButtonProps,
    AppFlyoutAnchorProps,
    AppSearchBoxProps,
    MenuDefinition,
    MenuItemControlProps,
    NextAppFlyoutAnchorProps,
    NextAppSearchBoxProps,
    NextRibbonSplitButtonProps,
    RibbonIconSet,
    RibbonSplitButtonProps,
    RibbonToggleButtonProps,
} from 'owa-acui';
import type { IButtonStyles, IKeytipProps } from '@fluentui/react';
import { getRibbonViewMode } from 'owa-command-ribbon-store';
import { getIsMsHighContrast } from 'owa-high-contrast';
/* eslint-disable-next-line @typescript-eslint/no-restricted-imports  -- (https://aka.ms/OWALintWiki)
 * Baseline. Do not copy and paste"
 *	> '../index' import is restricted from being used. */
import {
    AppButton,
    AppCheckBox,
    AppCompoundButton,
    AppFlyoutAnchor,
    AppSearchBox,
    AppSplitButton,
    AppToggleButton,
    getRibbonIconColor,
} from '../index';
import { logUsage } from 'owa-analytics';
import { getLightIconName, lightRibbonIconFont } from 'owa-command-ribbon-light-icon-font';
import { areWin32IconsRegistered } from 'owa-command-ribbon-win32-icon/lib/selectors/areWin32IconsRegistered';
import { fluentToWin32Map } from 'owa-command-ribbon-win32-icon/lib/registration/win32IconConfig';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import isWin32StylingEnabled from 'owa-theme-win32-style-store/lib/selectors/isWin32StylingEnabled';
import {
    heroButton,
    heroAppButton,
    heroSplitButtonMenu,
    heroSplitButtonPrimaryAction,
} from './heroButton.scss';
import { isSingleLineRibbon } from 'owa-command-ribbon-store/lib/selectors/isSingleLineRibbon';
import type { MailboxInfo } from 'owa-client-types';
import type { RibbonButtonOptions } from './ribbonControlOptions.types';
import { getRibbonScenarioName } from './ribbonScenarioContext';
import {
    getRibbonTelemetryControlCategory,
    type RibbonTelemetryControlCategory,
} from './getRibbonTelemetryControlCategory';

type OptionalIconSet = {
    iconSet?: RibbonIconSet;
};

/**
 * In order to make High Contrast more-or-less work, we need to remove all styling from our ribbon buttons.
 * If we are NOT in HC, we'll use any styling passed in, or fall back to default styles for the current
 * ribbon mode.
 */
function getAppButtonStyles(styles: IButtonStyles | undefined): IButtonStyles | undefined {
    return getIsMsHighContrast() ? undefined : styles;
}

const getComputedIconSet = owaComputedFn(function getComputedIconSet(
    icon: string | undefined
): RibbonIconSet | undefined {
    if (!icon) {
        return undefined;
    }

    // Transparently substitute Win32 icons when styling is enabled.
    // This means getControlInfo only needs to return the Fluent icon name — no Win32 awareness needed there.
    const isMultiLineRibbon = !isSingleLineRibbon();
    if (isWin32StylingEnabled() && areWin32IconsRegistered(isMultiLineRibbon)) {
        const win32IconSet = fluentToWin32Map[icon];
        if (win32IconSet) {
            return {
                icon16: win32IconSet.small,
                default: win32IconSet.default,
                icon32: win32IconSet.large,
            };
        }
    }

    let icon32;

    if (!isSingleLineRibbon() && lightRibbonIconFont.has(icon)) {
        icon32 = getLightIconName(icon);
    }

    return {
        default: icon,
        ...(icon32 && { icon32 }),
    };
});

const getComputedMenuIcon = owaComputedFn(function getComputedMenuIcon(
    icon: string | undefined
): string | undefined {
    const iconSet = getComputedIconSet(icon);
    return isSingleLineRibbon() ? iconSet?.default : iconSet?.icon16 ?? iconSet?.default;
});

/**
 * Returns the color that should be used for a given control's
 * icon. In order of priority:
 * 0. Outside the purview of this method, `overrides` parameter provided to createAppButton/createAppMenu/etc.
 * will be applied over the value returned from this method.
 * 1. If we're in high contrast mode, appropriate high contrast colors will be applied. This functionality is
 * provided by default by @1js/acui-ribbon-like when iconColor and style arguments are undefined.
 * 2. An explicit `iconColor` parameter provided to createAppButton/createAppMenuItem (or similar). Note this
 * value is used for all interactive states, so consider contrast requirements for rest, hover, pressed, etc.
 * across all themes (high contrast black, high contrast white, dark mode, light mode).
 * 3. Create and register an iconColorMap with initializeIconColorMap containing an entry for your control's `id`
 * @param id
 * @param iconColor
 */
function getIconColor(id: number | undefined, iconColor: string | undefined): string | undefined {
    if (getIsMsHighContrast()) {
        return undefined;
    }

    if (iconColor !== undefined) {
        return iconColor;
    }

    return id ? getRibbonIconColor(id) : undefined;
}

/**
 * Returns the string and parsed number representation of an id.
 * This is especially important for telemetry purposes, as many ids have appended data to them for uniqueness.
 * e.g.
 *  formatId('35_<editorId>') will return '35' here, which is just the base id.
 */
function formatId(id: string | number): [string, number, RibbonTelemetryControlCategory] {
    const idString = id.toString();
    const idNumber = typeof id === 'number' ? id : parseInt(id);
    const normalizedIdNumber = isNaN(idNumber) ? -1 : idNumber;

    return [
        idString,
        normalizedIdNumber,
        getRibbonTelemetryControlCategory(id, normalizedIdNumber),
    ];
}

/**
 * creates RibbonButtonProps object for use with the WAC Ribbon package
 */
export function createAppButton({
    id,
    label,
    onExecute,
    mailboxInfo,
    keytipProps,
    icon,
    iconColor,
    styles,
    overrides,
    isHero,
}: RibbonButtonOptions): AppButtonProps & OptionalIconSet {
    const [idString, idNumber, controlCategory] = formatId(id);
    const ribbonScenarioName = getRibbonScenarioName();

    const isFluentV9Enabled = isGlobalFeatureEnabled('mon-ribbon-fluent-v9-ribbon');
    const usingHeroStyle = isHero && isFluentV9Enabled && isSingleLineRibbon();

    return {
        type: AppButton,
        id: idString,
        label,
        icon: getComputedMenuIcon(icon), // Used for buttons within a Flyout menu
        iconSet: getComputedIconSet(icon), // Used for standard buttons on the ribbon (not in a flyout menu)
        iconColor: usingHeroStyle ? 'inherit' : getIconColor(idNumber, iconColor),
        styles: isFluentV9Enabled ? undefined : getAppButtonStyles(styles),
        className: usingHeroStyle ? `${heroButton} ${heroAppButton}` : undefined,
        keytipProps,
        onExecute: parameter => {
            logUsage('RibbonButtonInvoked', mailboxInfo, {
                ribbonId: idNumber,
                viewMode: getRibbonViewMode(),
                location: parameter.location,
                ribbonScenarioName,
                controlCategory,
            });
            onExecute(parameter);
        },
        ...overrides,
    };
}

/**
 * creates RibbonButtonProps object for use with the WAC Ribbon package. Button handles selected state.
 * @param id Unique id. To ensure uniqueness while keeping package size small, it is recommended that each ribbon maintain their own integer-based enum of unique entries, calling toString at runtime to generate string ids.
 * @param label Label displayed to the user
 * @param onExecute Function invoked when the button is executed
 * @param mailboxInfo MailboxInfo object for telemetry purposes
 * @param toggled Whether the button should be selected
 * @param keytipProps Optional keytip for button
 * @param icon Optional id of an icon to be displayed. Typically ControlIcons. It is the client's responsibility to ensure icon has been registered and is available at run-time.
 * @param iconColor Optional icon foreground color override
 * @param styles Optional style object.
 * @param overrides Optional object/array of other RibbonButtonProps props to override.
 */
export const createAppToggleButton = (
    id: number | string,
    label: string,
    onExecute: NonNullable<RibbonToggleButtonProps['onExecute']>,
    mailboxInfo: MailboxInfo,
    toggled: boolean,
    keytipProps?: IKeytipProps,
    icon?: string,
    iconColor?: string,
    styles?: IButtonStyles,
    overrides?: Partial<RibbonToggleButtonProps>
): RibbonToggleButtonProps & {
    icon?: string;
} => {
    const [idString, idNumber, controlCategory] = formatId(id);
    const ribbonScenarioName = getRibbonScenarioName();
    return {
        type: AppToggleButton,
        id: idString,
        label,
        icon: getComputedMenuIcon(icon), // Used for buttons within a Flyout menu
        iconSet: getComputedIconSet(icon), // Used for standard buttons on the ribbon (not in a flyout menu)
        iconColor: getIconColor(idNumber, iconColor),
        styles: getAppButtonStyles(styles),
        onExecute: parameter => {
            logUsage('RibbonToggleButtonInvoked', mailboxInfo, {
                ribbonId: idNumber,
                viewMode: getRibbonViewMode(),
                toggledState: toggled,
                ribbonScenarioName,
                controlCategory,
            });
            onExecute(parameter);
        },
        toggled,
        keytipProps,
        ...overrides,
    };
};

/**
 * creates MenuItemControlProps object for use with the WAC Ribbon package
 * @param id Unique id. To ensure uniqueness while keeping package size small, it is recommended that each ribbon maintain their own integer-based enum of unique entries, calling toString at runtime to generate string ids.
 * @param label Label displayed to the user
 * @param onExecute Function invoked when the button is executed
 * @param mailboxInfo MailboxInfo object for telemetry purposes
 * @param icon Optional id of an icon to be displayed. It is the client's responsibility to ensure icon has been registered and is available at run-time.
 * @param iconColor Optional icon foreground color override
 * @param styles Optional style object.
 * @param overrides Optional object/array of other MenuItemControlProps props to override.
 */
export const createAppMenuItem = (
    id: string | number,
    label: string,
    onExecute: () => void,
    mailboxInfo: MailboxInfo,
    icon?: string,
    iconColor?: string,
    styles?: IButtonStyles,
    /* eslint-disable-next-line owa-custom-rules/no-optional-any-parameter -- (https://aka.ms/OWALintWiki)
     * DO NOT COPY-PASTE! This code should be fixed by any developer touching this code
     *	> Optional function parameters should not have type "any". This can hide undefined/null references otherwise detectable by the transpiler. */
    overrides?: any
): MenuItemControlProps => {
    const [idString, idNumber, controlCategory] = formatId(id);
    const ribbonScenarioName = getRibbonScenarioName();
    return {
        type: AppButton,
        id: idString,
        label,
        icon: getComputedMenuIcon(icon), // Used for buttons within a Flyout menu
        iconSet: getComputedIconSet(icon), // Used for standard buttons on the ribbon (not in a flyout menu)
        iconColor: getIconColor(idNumber, iconColor),
        styles: getAppButtonStyles(styles),
        onExecute: parameter => {
            logUsage('RibbonButtonInvoked', mailboxInfo, {
                ribbonId: idNumber,
                viewMode: getRibbonViewMode(),
                location: parameter.location,
                ribbonScenarioName,
                controlCategory,
            });
            onExecute();
        },
        ...overrides,
    };
};

/**
 * creates MenuItemControlProps object for use with the WAC Ribbon package. Used for generated menu items (i.e. from a custom list of categories)
 * @param id Unique id. To ensure uniqueness while keeping package size small, it is recommended that each ribbon maintain their own integer-based enum of unique entries, calling toString at runtime to generate string ids.
 * @param label Label displayed to the user
 * @param onExecute Function invoked when the button is executed
 * @param mailboxInfo MailboxInfo object for telemetry purposes
 * @param keytipProps Optional keytip for menu item
 * @param icon Optional id of an icon to be displayed. It is the client's responsibility to ensure icon has been registered and is available at run-time.
 * @param iconColor Optional icon foreground color override
 * @param styles Optional styles object.
 * @param customTooltip Optional hover tooltip
 * @param overrides Optional object/array of other MenuItemControlProps props to override.
 */
export const createGeneratedMenuItem = (
    id: number | string,
    label: string,
    onExecute: () => void,
    mailboxInfo: MailboxInfo,
    keytipProps?: IKeytipProps,
    icon?: string,
    iconColor?: string,
    styles?: IButtonStyles,
    customTooltip?: string,
    /* eslint-disable-next-line owa-custom-rules/no-optional-any-parameter -- (https://aka.ms/OWALintWiki)
     * DO NOT COPY-PASTE! This code should be fixed by any developer touching this code
     *	> Optional function parameters should not have type "any". This can hide undefined/null references otherwise detectable by the transpiler. */
    overrides?: any
): MenuItemControlProps => {
    const [idString, idNumber, controlCategory] = formatId(id);
    const ribbonScenarioName = getRibbonScenarioName();
    return {
        type: AppButton,
        id: idString,
        label,
        icon: getComputedMenuIcon(icon), // Used for buttons within a Flyout menu
        iconColor: getIconColor(undefined /* id */, iconColor),
        styles: getAppButtonStyles(styles),
        customTooltip,
        onExecute: parameter => {
            logUsage('RibbonButtonInvoked', mailboxInfo, {
                ribbonId: idNumber,
                viewMode: getRibbonViewMode(),
                location: parameter.location,
                ribbonScenarioName,
                controlCategory,
            });
            onExecute();
        },
        keytipProps,
        ...overrides,
    };
};

/**
 * creates MenuItemControlProps object for use with the WAC Ribbon package. Used for generated menu items (i.e. from a custom list of categories). Button handles selected state
 * @param id Unique id. To ensure uniqueness while keeping package size small, it is recommended that each ribbon maintain their own integer-based enum of unique entries, calling toString at runtime to generate string ids.
 * @param label Label displayed to the user
 * @param onExecute Function invoked when the button is executed
 * @param mailboxInfo MailboxInfo object for telemetry purposes
 * @param toggled Whether the button should be selected
 * @param icon Optional id of an icon to be displayed. It is the client's responsibility to ensure icon has been registered and is available at run-time.
 * @param iconColor Optional icon foreground color override
 * @param styles Optional style object.
 * @param overrides Optional object/array of other MenuItemControlProps props to override.
 */
export const createGeneratedToggleMenuItem = (
    id: number | string,
    label: string,
    onExecute: NonNullable<RibbonToggleButtonProps['onExecute']>,
    mailboxInfo: MailboxInfo,
    toggled: boolean,
    icon?: string,
    iconColor?: string,
    styles?: IButtonStyles,
    /* eslint-disable-next-line owa-custom-rules/no-optional-any-parameter -- (https://aka.ms/OWALintWiki)
     * DO NOT COPY-PASTE! This code should be fixed by any developer touching this code
     *	> Optional function parameters should not have type "any". This can hide undefined/null references otherwise detectable by the transpiler. */
    overrides?: any
): MenuItemControlProps => {
    const [idString, idNumber, controlCategory] = formatId(id);
    const ribbonScenarioName = getRibbonScenarioName();
    return {
        type: AppToggleButton,
        id: idString,
        label,
        icon: getComputedMenuIcon(icon),
        iconColor,
        styles: getAppButtonStyles(styles),
        onExecute: parameter => {
            logUsage('RibbonButtonInvoked', mailboxInfo, {
                ribbonId: idNumber,
                viewMode: getRibbonViewMode(),
                location: parameter.location,
                ribbonScenarioName,
                controlCategory,
            });
            onExecute(parameter);
        },
        toggled,
        ...overrides,
    };
};

/**
 * Creates RibbonFlyoutAnchorProps object for use with the WAC Ribbon package.
 * @param id Unique id. To ensure uniqueness while keeping package size small, it is recommended that each ribbon maintain their own integer-based enum of unique entries, calling toString at runtime to generate string ids.
 * @param label Label displayed to the user
 * @param menuDefinition Object defining the menu shown when the flyout is invoked
 * @param mailboxInfo Mailbox information for telemetry purposes
 * @param toggled Whether the button should be selected
 * @param keytipProps Optional keytip for menu item
 * @param icon Optional id of an icon to be displayed. It is the client's responsibility to ensure icon has been registered and is available at run-time.
 * @param iconColor Optional icon foreground color override
 * @param styles Optional styles object.
 * @param overrides Optional object/array of other MenuItemControlProps props to override.
 */
export const createAppFlyoutAnchor = (
    id: string | number,
    label: string,
    menuDefinition: MenuDefinition,
    mailboxInfo: MailboxInfo,
    keytipProps?: IKeytipProps,
    icon?: string,
    iconColor?: string,
    styles?: IButtonStyles,
    overrides: Partial<AppFlyoutAnchorProps> & Partial<NextAppFlyoutAnchorProps> = {}
): AppFlyoutAnchorProps & OptionalIconSet => {
    const { onExecute, ...remainingOverrides } = overrides;
    const [idString, idNumber, controlCategory] = formatId(id);
    const ribbonScenarioName = getRibbonScenarioName();
    const isFluentV9Enabled = isGlobalFeatureEnabled('mon-ribbon-fluent-v9-ribbon');

    return {
        type: AppFlyoutAnchor,
        id: idString,
        label,
        keytipProps,
        icon: getComputedMenuIcon(icon),
        iconSet: getComputedIconSet(icon),
        iconColor: getIconColor(idNumber, iconColor),
        styles: isFluentV9Enabled ? undefined : getAppButtonStyles(styles),
        menuDefinition,
        shadowRenderMenu: false,
        onExecute: parameter => {
            logUsage('RibbonButtonInvoked', mailboxInfo, {
                ribbonId: idNumber,
                viewMode: getRibbonViewMode(),
                location: parameter.location,
                ribbonScenarioName,
                controlCategory,
            });
            onExecute?.(parameter);
        },
        isV9ContextMenuEnabled: isFluentV9Enabled,
        isV9FloatieEnabled: isFluentV9Enabled,
        ...remainingOverrides,
    };
};

/**
 * creates RibbonSplitButtonProps object for use with the WAC Ribbon package.
 * @param id Unique id. To ensure uniqueness while keeping package size small, it is recommended that each ribbon maintain their own integer-based enum of unique entries, calling toString at runtime to generate string ids.
 * @param label Label displayed to the user
 * @param menuDefinition Object defining the menu shown when the flyout is invoked
 * @param onExecute Function invoked when the button is executed
 * @param mailboxInfo MailboxInfo object for telemetry purposes
 * @param keytipProps Optional keytip for menu item
 * @param icon Optional id of an icon to be displayed. It is the client's responsibility to ensure icon has been registered and is available at run-time.
 * @param iconColor Optional icon foreground color override
 * @param styles Optional styles object.
 * @param overrides Optional object of other RibbonSplitButtonProps props to override.
 * @param isHero Indicates a "Hero" style button in SLR
 */
export const createAppSplitButton = (
    id: number | string,
    label: string,
    menuDefinition: MenuDefinition,
    onExecute: NonNullable<RibbonSplitButtonProps['onExecute']>,
    mailboxInfo: MailboxInfo,
    keytipProps?: IKeytipProps,
    icon?: string,
    iconColor?: string,
    styles?: IButtonStyles,
    overrides: Partial<RibbonSplitButtonProps> = {},
    isHero?: boolean
): RibbonSplitButtonProps &
    Partial<NextRibbonSplitButtonProps> & {
        icon?: string;
    } => {
    const { onMenuButtonExecute, ...remainingOverrides } = overrides;
    const [idString, idNumber, controlCategory] = formatId(id);
    const ribbonScenarioName = getRibbonScenarioName();

    const isFluentV9Enabled = isGlobalFeatureEnabled('mon-ribbon-fluent-v9-ribbon');
    const usingHeroStyle = isHero && isFluentV9Enabled && isSingleLineRibbon();

    return {
        type: AppSplitButton,
        id: idString,
        label,
        icon,
        iconSet: getComputedIconSet(icon),
        iconColor: usingHeroStyle ? 'inherit' : getIconColor(idNumber, iconColor),
        onExecute: parameter => {
            logUsage('RibbonButtonInvoked', mailboxInfo, {
                ribbonId: idNumber,
                viewMode: getRibbonViewMode(),
                location: parameter.location,
                ribbonScenarioName,
                controlCategory,
            });
            onExecute(parameter);
        },
        keytipProps,
        styles: getAppButtonStyles(styles),
        ...(usingHeroStyle && {
            menuButton: { className: `${heroButton} ${heroSplitButtonMenu}` },
            primaryActionButton: { className: `${heroButton} ${heroSplitButtonPrimaryAction}` },
        }),
        menuDefinition,
        shadowRenderMenu: false,
        onMenuButtonExecute: parameter => {
            logUsage('RibbonSplitButtonMenuInvoked', mailboxInfo, {
                ribbonId: idNumber,
                label,
                viewMode: getRibbonViewMode(),
                ribbonScenarioName,
                controlCategory,
            });
            onMenuButtonExecute?.(parameter);
        },
        ...remainingOverrides,
    };
};

/**
 * creates AppCompoundButtonProps object for use with the WAC Ribbon package
 * @param id Unique id. To ensure uniqueness while keeping package size small, it is recommended that each ribbon maintain their own integer-based enum of unique entries, calling toString at runtime to generate string ids.
 * @param label Label displayed to the user
 * @param description Secondary information displayed to the user
 * @param onExecute Function invoked when the button is executed
 * @param mailboxInfo Mailbox information for telemetry purposes
 * @param icon id of an icon to be displayed. It is the client's responsibility to ensure icon has been registered and is available at run-time.
 * @param styles Optional styles object.
 * @param customTooltip Optional hover tooltip
 */
export const createAppCompoundButton = (
    id: number | string,
    label: string,
    description: string,
    onExecute: NonNullable<AppCompoundButtonProps['onExecute']>,
    mailboxInfo: MailboxInfo,
    icon: string,
    styles?: IButtonStyles,
    customTooltip?: string
): AppCompoundButtonProps => {
    const [idString, idNumber, controlCategory] = formatId(id);
    const ribbonScenarioName = getRibbonScenarioName();

    return {
        type: AppCompoundButton,
        id: idString,
        label,
        description,
        icon,
        onExecute: parameter => {
            logUsage('RibbonButtonInvoked', mailboxInfo, {
                ribbonId: idNumber,
                viewMode: getRibbonViewMode(),
                location: parameter.location,
                ribbonScenarioName,
                controlCategory,
            });
            onExecute(parameter);
        },
        styles,
        customTooltip,
    };
};

/**
 * creates AppSearchBoxProps object for use with the WAC Ribbon package
 * @param id Unique id. To ensure uniqueness while keeping package size small, it is recommended that each ribbon maintain their own integer-based enum of unique entries, calling toString at runtime to generate string ids.
 * @param label Label displayed to the user
 * @param onChangeCallback Callback when the searchbox contents change. Empty box returns "undefined" newValue
 * @param mailboxInfo MailboxInfo object for telemetry purposes
 * @param keytipProps Optional keytip for search box
 * @param icon Optional id of an icon to be displayed. Typically ControlIcons. It is the client's responsibility to ensure icon has been registered and is available at run-time.
 * @param iconColor Optional icon foreground color override
 * @param typeProp Optional search box type override. This is added to fix issue Japanese input method: WI: 219802
 * @param overrides Optional object/array of other AppSearchBoxProps props to override. Typed against
 * the Fluent v9 ("next") search box, which is what ACUI's AppSearchBox always renders.
 */
export const createAppSearchBox = (
    id: number | string,
    label: string,
    onChangeCallback: (event?: React.ChangeEvent<HTMLInputElement>, newValue?: string) => void,
    mailboxInfo: MailboxInfo,
    keytipProps?: IKeytipProps,
    icon?: string,
    iconColor?: string,
    typeProp?: string,
    overrides: Partial<NextAppSearchBoxProps> = {}
): AppSearchBoxProps & {
    keytipProps?: IKeytipProps;
} => {
    const { onSearch, ...remainingOverrides } = overrides;
    const [idString, idNumber, controlCategory] = formatId(id);
    const ribbonScenarioName = getRibbonScenarioName();

    return {
        type: AppSearchBox,
        id: idString,
        label,
        onChange: (event, newValue) => {
            logUsage('RibbonSearchboxOnChange', mailboxInfo, {
                ribbonId: idNumber,
                viewMode: getRibbonViewMode(),
                ribbonScenarioName,
                controlCategory,
            });
            onChangeCallback(event, newValue);
        },
        keytipProps,
        icon,
        iconColor: getIconColor(idNumber, iconColor),
        onSearch: newValue => {
            logUsage('RibbonSearchboxInvoked', mailboxInfo, {
                ribbonId: idNumber,
                viewMode: getRibbonViewMode(),
                ribbonScenarioName,
                controlCategory,
            });

            onSearch?.(newValue);
        },
        typeProp,
        ...remainingOverrides,
    };
};

/**
 * creates AppCheckboxProps object for use with the WAC Ribbon package
 * @param id Unique id. To ensure uniqueness while keeping package size small, it is recommended that each ribbon maintain their own integer-based enum of unique entries, calling toString at runtime to generate string ids.
 * @param label Label displayed to the user
 * @param onExecute Function invoked when the button is executed
 * @param mailboxInfo MailboxInfo object for telemetry purposes
 * @param isChecked indicates if the checkbox is checked or not
 * @param keytipProps Optional keytip for the checkbox
 * @param styles Optional styles object.
 * @param overrides Optional object/array of other AppCheckboxProps props to override.
 */
export const createAppCheckBox = (
    id: number | string,
    label: string,
    onExecute: NonNullable<AppCheckboxProps['onExecute']>,
    mailboxInfo: MailboxInfo,
    isChecked: boolean,
    keytipProps?: IKeytipProps,
    styles?: AppCheckboxProps['styles'],
    overrides?: Partial<AppCheckboxProps>
): AppCheckboxProps => {
    const [idString, idNumber, controlCategory] = formatId(id);
    const ribbonScenarioName = getRibbonScenarioName();
    return {
        type: AppCheckBox,
        id: idString,
        label,
        onExecute: (parameter: AppCheckboxOnExecuteParameter) => {
            logUsage('RibbonCheckboxButtonInvoked', mailboxInfo, {
                ribbonId: idNumber,
                viewMode: getRibbonViewMode(),
                checked: isChecked,
                ribbonScenarioName,
                controlCategory,
            });
            onExecute(parameter);
        },
        checked: isChecked,
        keytipProps,
        styles,
        ...overrides,
    };
};
